import {
	EventCustomFeedbackCodes,
	EventCustomOverlayDto,
	EventLevels,
	EventLevelsValues,
	EventTypes,
	EventTypesValues,
} from 'defs/events';
import { getCustomOverlayTargets } from 'defs/notifications';
import { toUnix } from 'helper/dateTime';
import moment from 'moment';
import { EventType, NormalizedEventsType, RootStateType } from '.';
import { getCreationDate } from '../profile';
import { getCurrentViewID } from '../view';
import { CustomOverlayTrigger, CustomOverlayType } from './events';

export const getEvents = (rootState: RootStateType): NormalizedEventsType => {
	return rootState.events.events.filter((notification: Object | void) => {
		return !!notification;
	});
};

export const filterCustomOverlays = (
	customOverlays: NormalizedEventsType<CustomOverlayType>,
	viewId: string | null,
	profileCreationDateValue: string | null
): NormalizedEventsType<CustomOverlayType> => {
	return customOverlays.filter((overlay) => {
		// filter out overlays until `profile` and `view` are known
		if (!viewId || !profileCreationDateValue) {
			return false;
		}

		// check routes
		if (overlay.trigger !== 'ANY' && overlay.trigger !== CustomOverlayTrigger[viewId!]) {
			return false;
		}

		// check profile registration date. backend already filters out dateFrom/dateUntil.
		// - remove time from overlay `dateFrom` in order to compare only the date
		const overlayDateFromDate = new Date(moment(overlay.dateFrom).format('YYYY-MM-DD'));
		const profileCreationDate = new Date(profileCreationDateValue);

		if (overlay.onlyExistingUser && toUnix(profileCreationDate) > toUnix(overlayDateFromDate)) {
			return false;
		}

		return true;
	});
};

export const mapCustomOverlays = (customOverlays: NormalizedEventsType<CustomOverlayType>): EventCustomOverlayDto[] => {
	return customOverlays.map((overlay) => {
		return {
			overwrites: {
				text: overlay.text,
				title: overlay.title,
				targets: getCustomOverlayTargets(overlay),
			},
			code: EventCustomFeedbackCodes.CustomOverlay,
			eventLevel: EventLevels.NotAvailable as EventLevelsValues,
			eventType: EventTypes.Feedback as EventTypesValues,
			reactId: overlay.reactId,
			payload: {
				id: overlay.id,
			},
		};
	});
};

export const getCustomOverlays = (rootState: RootStateType): EventCustomOverlayDto[] => {
	const customOverlays = rootState.events.customOverlays;
	const viewId = getCurrentViewID(rootState as any);
	const profileCreationDateValue = getCreationDate(rootState as any);
	return mapCustomOverlays(filterCustomOverlays(customOverlays, viewId, profileCreationDateValue));
};

export const getPoints = (rootState: RootStateType): NormalizedEventsType => {
	return (
		getEvents(rootState)
			.map((event: EventType): any => {
				return event.eventType === EventTypes.Point ? event : null;
			})
			.filter((event: EventType) => !!event) || []
	);
};

export const getStickers = (rootState: RootStateType): NormalizedEventsType => {
	return (
		getEvents(rootState)
			.map((event: EventType): any => {
				return event.eventType === EventTypes.Sticker ? event : null;
			})
			.filter((event: EventType) => !!event) || []
	);
};

export const getBadges = (rootState: RootStateType): NormalizedEventsType => {
	return (
		getEvents(rootState)
			.map((event: EventType): any => {
				return event.eventType === EventTypes.Badge ? event : null;
			})
			.filter((event: EventType) => !!event) || []
	);
};

export const getLevelUps = (rootState: RootStateType): NormalizedEventsType => {
	return (
		getEvents(rootState)
			.map((event: EventType): any => {
				return event.eventType === EventTypes.LevelUp ? event : null;
			})
			.filter((event: EventType) => !!event) || []
	);
};

export const getProgramSummaries = (rootState: RootStateType): NormalizedEventsType => {
	return (
		getEvents(rootState)
			.map((event: EventType): any => {
				return event.eventType === EventTypes.ProgramSummary ? event : null;
			})
			.filter((event: EventType) => !!event) || []
	);
};

export const getFeedbacks = (rootState: RootStateType): NormalizedEventsType => {
	return (
		getEvents(rootState)
			.map((event: EventType): any => {
				return event.eventType === EventTypes.Feedback ? event : null;
			})
			.filter((event: EventType) => !!event) || []
	);
};

export const getImages = (rootState: RootStateType): NormalizedEventsType => {
	return (
		getEvents(rootState)
			.map((event: EventType): any => {
				return event.eventType === EventTypes.Image ? event : null;
			})
			.filter((event: EventType) => !!event) || []
	);
};

export const getWarnings = (rootState: RootStateType): NormalizedEventsType => {
	return (
		getEvents(rootState)
			.map((event: EventType): any => {
				return event.eventType === EventTypes.Warning ? event : null;
			})
			.filter((event: EventType) => !!event) || []
	);
};

export const getMessages = (rootState: RootStateType): NormalizedEventsType => {
	return (
		getEvents(rootState)
			.map((event: EventType): any => {
				return event.eventType === EventTypes.Message ? event : null;
			})
			.filter((event: EventType) => !!event) || []
	);
};

export const getProgram = (rootState: RootStateType) => rootState.events.program;

export const getLevelPoints = (rootState: RootStateType) => {
	if (rootState.events.points) {
		return rootState.events.points;
	}
	return '';
};

export const getLevel = (rootState: RootStateType) => {
	if (rootState.events.level) {
		return rootState.events.level;
	}
	return '';
};
